import * as React from 'react'
import * as LabelPrimitive from '@radix-ui/react-label'
import { Slot } from '@radix-ui/react-slot'
import {
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
  FormProvider,
  useFormContext,
} from 'react-hook-form'
import { Label } from './label'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from './select'
import { TriangleAlert } from 'lucide-react'
import { cn } from '@ui/lib/utils'

const Form = FormProvider

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName
}

const FormFieldContext = React.createContext<FormFieldContextValue>(
  {} as FormFieldContextValue,
)

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  )
}

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext)
  const itemContext = React.useContext(FormItemContext)

  const formContext = useFormContext()

  if (!fieldContext) {
    console.error('useFormContext must be used within a FormProvider')
  }

  const { getFieldState, formState } = formContext

  const fieldState = getFieldState(fieldContext.name, formState)

  if (!fieldContext) {
    throw new Error('useFormField should be used within <FormField>')
  }

  const { id } = itemContext

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  }
}

type FormItemContextValue = {
  id: string
}

const FormItemContext = React.createContext<FormItemContextValue>(
  {} as FormItemContextValue,
)

const FormItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const id = React.useId()

  return (
    <FormItemContext.Provider value={{ id }}>
      <div ref={ref} className={cn('mt-xxs', className)} {...props} />
    </FormItemContext.Provider>
  )
})
FormItem.displayName = 'FormItem'

const FormLabel = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root> & {
    isRequired?: boolean
  }
>(({ className, isRequired, ...props }, ref) => {
  const { formItemId } = useFormField()

  return (
    <Label
      ref={ref}
      className={cn('mb-xxs text-sm font-medium', className)}
      htmlFor={formItemId}
      {...props}
    >
      {isRequired && '*'}
      {props.children}
    </Label>
  )
})
FormLabel.displayName = 'FormLabel'

const FormControl = React.forwardRef<
  React.ElementRef<typeof Slot>,
  React.ComponentPropsWithoutRef<typeof Slot>
>(({ ...props }, ref) => {
  const { error, formItemId, formDescriptionId, formMessageId } = useFormField()

  return (
    <Slot
      ref={ref}
      id={formItemId}
      aria-describedby={
        !error
          ? `${formDescriptionId}`
          : `${formDescriptionId} ${formMessageId}`
      }
      aria-invalid={!!error}
      {...props}
    />
  )
})
FormControl.displayName = 'FormControl'

const FormDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => {
  const { formDescriptionId } = useFormField()

  return (
    <p
      ref={ref}
      id={formDescriptionId}
      className={cn('text-sm text-gray-500', className)}
      {...props}
    />
  )
})
FormDescription.displayName = 'FormDescription'

const FormMessage = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, children, ...props }, ref) => {
  const { error, formMessageId } = useFormField()
  const body = error ? String(error?.message) : children

  if (!body) {
    return null
  }

  return (
    <p
      ref={ref}
      id={formMessageId}
      className={cn(
        'flex items-start gap-xxxs pt-xxs text-sm text-red-500',
        className,
      )}
      {...props}
    >
      <TriangleAlert size={16} className="text-red-500" /> {body}
    </p>
  )
})
FormMessage.displayName = 'FormMessage'

const FormSelect = React.forwardRef<
  React.ElementRef<typeof Select>,
  React.ComponentPropsWithoutRef<typeof Select>
>(({ ...props }, ref) => {
  const { name } = useFormField()
  return <Select ref={ref} name={name} {...props} />
})
FormSelect.displayName = 'FormSelect'

const FormSelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectTrigger>,
  React.ComponentPropsWithoutRef<typeof SelectTrigger>
>(({ className, children, ...props }, ref) => {
  const { formItemId } = useFormField()
  return (
    <SelectTrigger ref={ref} id={formItemId} className={className} {...props}>
      {children}
    </SelectTrigger>
  )
})
FormSelectTrigger.displayName = 'FormSelectTrigger'

const FormSelectContent = React.forwardRef<
  React.ElementRef<typeof SelectContent>,
  React.ComponentPropsWithoutRef<typeof SelectContent>
>((props, ref) => <SelectContent ref={ref} {...props} />)
FormSelectContent.displayName = 'FormSelectContent'

const FormSelectItem = React.forwardRef<
  React.ElementRef<typeof SelectItem>,
  React.ComponentPropsWithoutRef<typeof SelectItem>
>((props, ref) => <SelectItem ref={ref} {...props} />)
FormSelectItem.displayName = 'FormSelectItem'

const FormSelectGroup = React.forwardRef<
  React.ElementRef<typeof SelectGroup>,
  React.ComponentPropsWithoutRef<typeof SelectGroup>
>((props, ref) => <SelectGroup ref={ref} {...props} />)
FormSelectGroup.displayName = 'FormSelectGroup'

const FormSelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectLabel>,
  React.ComponentPropsWithoutRef<typeof SelectLabel>
>((props, ref) => <SelectLabel ref={ref} {...props} />)
FormSelectLabel.displayName = 'FormSelectLabel'

const FormSelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectSeparator>,
  React.ComponentPropsWithoutRef<typeof SelectSeparator>
>((props, ref) => <SelectSeparator ref={ref} {...props} />)
FormSelectSeparator.displayName = 'FormSelectSeparator'

const FormSelectValue = React.forwardRef<
  React.ElementRef<typeof SelectValue>,
  React.ComponentPropsWithoutRef<typeof SelectValue>
>((props, ref) => <SelectValue ref={ref} {...props} />)
FormSelectValue.displayName = 'FormSelectValue'

export {
  useFormField,
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
  FormField,
  FormSelect,
  FormSelectTrigger,
  FormSelectContent,
  FormSelectItem,
  FormSelectGroup,
  FormSelectLabel,
  FormSelectSeparator,
  FormSelectValue,
}
